import { useRouter } from 'next/router';

import { Alert, Anchor, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { getClient } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyContactEmailWebApiHandler_purge } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';

const LEARN_MORE_LINK =
  'https://www.zillow.help/article/why-am-i-not-receiving-emails-from-zillow-rental-manager-zd30237400767763';

const ContactEmailDeliverabilityAlert = () => {
  const router = useRouter();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { mutate: purgeContactEmail, isError: hasEmailPurgeError } =
    useRentalManagerUserPropertyContactEmailWebApiHandler_purge({
      httpClient: getClient()?.extend({ timeout: 5000 }),
      meta: {
        invalidates: ['listingDetails'],
      },
      onSuccess: () => {
        enqueueToast(<Toast tone="success">Email confirmed</Toast>);
      },
    });

  const {
    contactEmail,
    isDeliverable = true,
    isPurgeLimitExceeded,
    supportEmail,
  } = listingDetails?.contactEmailDeliverability || {};

  const handleConfirmClick = () => {
    purgeContactEmail({ getParams: { listingAlias } });
  };

  const renderDefaultContent = () => {
    return (
      <span data-testid="DeliveryIssueContent">
        We’ve detected a deliverability issue with your contact email: {contactEmail}.
      </span>
    );
  };

  const renderPurgeLimitExceededContent = () => {
    return (
      <div data-testid="PurgeLimitExceededContent">
        <span>We’ve detected a deliverability issue with your contact email: {contactEmail}. Contact </span>
        <Anchor href={`mailto:${supportEmail}`} target="_blank" rel="noopener noreferrer">
          customer support
        </Anchor>
        <span>.</span>
      </div>
    );
  };

  const renderBody = () => {
    return isPurgeLimitExceeded ? renderPurgeLimitExceededContent() : renderDefaultContent();
  };

  const renderEmailPurgeError = () => {
    return (
      <div>
        <span>Action failed.&nbsp;</span>
        <TextButton onClick={handleConfirmClick}>Try again</TextButton>
      </div>
    );
  };

  const renderDefaultActionButton = () => {
    return (
      <div>
        <TextButton onClick={handleConfirmClick}>Confirm</TextButton>
        <span>
          &nbsp;the email is correct or&nbsp;
          <Anchor href={LEARN_MORE_LINK} target="_blank" rel="noreferrer">
            learn more
          </Anchor>
          .
        </span>
      </div>
    );
  };

  const renderActionButton = () => {
    return hasEmailPurgeError ? renderEmailPurgeError() : renderDefaultActionButton();
  };

  if (isDeliverable) {
    return null;
  }

  return (
    <Alert
      data-testid="ContactEmailDeliverabilityAlert"
      tone="critical"
      actionButton={!isPurgeLimitExceeded && renderActionButton()}
    >
      {renderBody()}
    </Alert>
  );
};

export default ContactEmailDeliverabilityAlert;
